// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

// TMI - content type and query definitions
import { Author } from "@tmi/types/entities";

import { authors, cache, CacheClosedError, CacheError } from "@/services/cache";
import { loadAuthorSeedData } from "@/services/seed-service";
import { createInitLatch } from "@/stores/init-latch";
import { createCatalogIndex, markRawRows } from "@/stores/catalog-index";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allAuthors.value.length > 0 );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );
// UX roadmap #70: true once an initialize() has finished, from the cache, the seed or a failure. An empty catalog
// alone cannot tell "not loaded yet" from "loaded, and this author is not in it", so the author page 404ed until the
// load landed. Left false on the quiet CacheClosedError stop: that tab is reloading, so the page keeps its skeleton.
const settled = ref< boolean >( false );
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();

// Rank 11: a read-only catalog of raw rows, always replaced by assignment; hydration resolves one author per item.
const allAuthors = shallowRef< Author[] >( [] );
const authorsById = createCatalogIndex( allAuthors, ( author: Author ) => author.id );
const authorsBySlug = createCatalogIndex( allAuthors, ( author: Author ) => author.slug );

export const useAuthorsStore = defineStore( 'authors', () => {
    function reset(): void {
        allAuthors.value = [];
        authorsById.clear();
        authorsBySlug.clear();
        loading.value = false;
        refreshing.value = false;
        settled.value = false;
        initLatch.clear();
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value || initLatch.isHalted() ) {
            logger.debug( '[ Authors ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Authors ] Checking the local database for any authors...` );

            loading.value = true;

            const t0: number = performance.now();

            // Wait for the database to open before the first read.
            await cache.ready();

            // If anything exists within the local database, load it
            if ( !await authors.isEmpty() ) {
                allAuthors.value = markRawRows( await authors.all() );
                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allAuthors.value.length } authors in ${ t1 - t0 } ms` );
            } else {
                allAuthors.value = markRawRows( await loadAuthorSeedData() );

                const t1: number = performance.now();
                logger.info( `Fetching & mapping ${ allAuthors.value.length } author items took ${ t1 - t0 } ms` );
            }
            settled.value = true;
        } catch ( e: any ) {
            if ( e instanceof CacheClosedError ) { initLatch.halt( e ); return; }   // a newer build upgraded the cache; this tab is reloading
            logger.error( JSON.stringify( e.message ) );
            reset();
            // UX roadmap #70: with the cache unavailable (IndexedDB blocked, an open error) the catalog used to stay
            // empty until reset(), so every author page was a permanent 404. The bundled seed stands in. Only for a
            // cache failure: a failed seed chunk is not retried here (it was just reported, and would be twice).
            if ( e instanceof CacheError ) {
                loading.value = true;   // reset() cleared it; nothing may re-enter while the seed loads
                allAuthors.value = await loadSeedFallback();
            }
            initLatch.halt( e );   // after reset(), which clears it: a cache failure stays latched
            settled.value = true;
        } finally {
            loading.value = false;
            refreshing.value = false;
        }
    }

    /** The seed, or nothing when its chunk fails too (loadAuthorSeedData has reported that). */
    async function loadSeedFallback(): Promise< Author[] > {
        try {
            return markRawRows( await loadAuthorSeedData() );
        } catch {
            return [];
        }
    }

    /**
     * Retrieves an Author object by its contentful sys.id value or returns undefined if the given
     * sys.id value is not found within the in-memory collection.
     *
     * @param {string} sysId - the contentful assigned sys.id (unique identifier within the contentful space/environment
     * @returns {Author | undefined} - the Author object previously loaded
     */
    function authorById( sysId: string ) : Author | undefined {
        return authorsById.get( sysId );
    }

    function authorBySlug( slug: string ): Author | undefined {
        return authorsBySlug.get( slug );
    }

    return {
        // ACTIONS
        authorById,
        authorBySlug,
        initialize,
        reset,

        // GETTERS
        allAuthors,
        isLoading: computed< boolean >( () => loading.value ),
        isSettled: computed< boolean >( () => settled.value )
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useAuthorsStore, import.meta.hot ) );
}
